import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, AppWindow, CheckCircle2, Loader2, Plus, Trash2, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { SearchableMultiSelect } from '@/components/ui/searchable-multi-select';
import { SearchableSelect } from '@/components/ui/searchable-select';
import { useToast } from '@/hooks/use-toast';
import { getMetaApps } from '@/services/api';
import { getCurrentUser } from '@/services/api/auth';
import { getMetaPages, getUsersPagesMap } from '@/services/api/connections';
import {
    deleteAppPageGroup,
    extractPagesWithoutToken,
    listAppPageGroups,
    removePageFromGroup,
    saveAppPageGroup,
    type PageWithoutToken,
} from '@/services/api/app-page-groups';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
} from '@/components/ui/dialog';
import FiltroUsuarioAdmin, { useFiltroUsuarioAdmin } from '@/components/FiltroUsuarioAdmin';

/**
 * Grupo de App: associação manual e fixa entre 1 app Meta e N páginas, para
 * eliminar o round-robin dinâmico de app usado hoje no envio de broadcast e
 * na aprovação de utility templates. Sem essa associação, nada garante
 * distribuição real entre apps — caso real que motivou esta tela:
 * group_broadcast_id=2302, 343 de 347 páginas caíram no MESMO app, causando
 * 372.695 erros de rate limit (código 613) enquanto outros apps ativos
 * ficaram ociosos.
 */
export default function AppPageGroupsList() {
    const { toast } = useToast();
    const queryClient = useQueryClient();
    const [editingAppId, setEditingAppId] = useState<number | null>(null);
    const [creatingAppId, setCreatingAppId] = useState<number | null>(null);
    const [draftPageIds, setDraftPageIds] = useState<string[]>([]);
    const [groupToDelete, setGroupToDelete] = useState<number | null>(null);
    const [activeCategoryIds, setActiveCategoryIds] = useState<string[]>([]);
    const [selectedConnectedUserId, setSelectedConnectedUserId] = useState('all');
    const [pagesWithoutToken, setPagesWithoutToken] = useState<PageWithoutToken[] | null>(null);
    const [pendingSave, setPendingSave] = useState<{ appId: number; pageIds: string[] } | null>(null);

    // Grupo de App é por (app, usuário). Admin vê os de todos, só leitura; com
    // um dono no filtro, edita os grupos daquele dono como ele.
    const { isAdmin, usuarioId } = useFiltroUsuarioAdmin();
    const donoParam = usuarioId ?? undefined;
    const vendoTodos = isAdmin && donoParam == null;

    const { data: groups = [], isLoading: loadingGroups } = useQuery({
        queryKey: ['app-page-groups', donoParam ?? null],
        queryFn: () => listAppPageGroups(donoParam),
    });

    const { data: apps = [], isLoading: loadingApps } = useQuery({
        queryKey: ['meta-apps-all'],
        queryFn: () => getMetaApps('ativos'),
    });

    const { data: pages = [], isLoading: loadingPages } = useQuery({
        queryKey: ['meta-pages-all'],
        queryFn: getMetaPages,
    });

    // Mesmo filtro "Conectado por" da tela de Páginas — mapa de perfil (meta_user)
    // conectado -> páginas vinculadas via meta_page_tokens. Útil aqui porque é
    // exatamente a mesma fonte usada na validação de token do grupo.
    const { data: connectedUsers = [] } = useQuery({
        queryKey: ['meta-users-pages-map'],
        queryFn: getUsersPagesMap,
    });

    const connectedUserOptions = useMemo(
        () =>
            connectedUsers.map(u => ({
                value: String(u.meta_user_id),
                label: u.meta_user_name ? `${u.meta_user_name} (${u.app_name ?? 'sem app'})` : `Perfil #${u.meta_user_id}`,
            })),
        [connectedUsers]
    );

    const connectedUserPageIds = useMemo(() => {
        if (selectedConnectedUserId === 'all') return null;
        const entry = connectedUsers.find(u => String(u.meta_user_id) === selectedConnectedUserId);
        return entry ? new Set(entry.pages.map(p => p.page_id)) : new Set<string>();
    }, [selectedConnectedUserId, connectedUsers]);

    const categoryOptions = useMemo(
        () =>
            Array.from(
                new Map(
                    pages
                        .filter(p => p.internal_category_id && p.internal_category_name)
                        .map(p => [p.internal_category_id, p.internal_category_name])
                ).entries()
            ).map(([id, name]) => ({ value: String(id), label: name as string })),
        [pages]
    );

    const groupedPageIds = useMemo(() => {
        const ids = new Set<string>();
        for (const g of groups) {
            for (const p of g.pages) ids.add(p.page_id);
        }
        return ids;
    }, [groups]);

    // page_id -> app do grupo atual dela, usado só quando o filtro "Conectado
    // por" está ativo: nesse caso mostramos a página mesmo já alocada em
    // outro app (pra permitir mover), com esse indicador visual.
    const currentGroupByPageId = useMemo(() => {
        const map = new Map<string, { appId: number; appName: string | null }>();
        for (const g of groups) {
            for (const p of g.pages) map.set(p.page_id, { appId: g.app_id, appName: g.app_name });
        }
        return map;
    }, [groups]);

    // GET /meta/apps devolve os apps de todos os usuários; aqui só entram os do
    // dono da tela (o próprio usuário, ou quem o admin escolheu no filtro).
    const donoId = donoParam ?? getCurrentUser()?.id;
    const activeApps = useMemo(
        () => apps.filter(a => a.is_active && a.user_id === donoId),
        [apps, donoId]
    );

    const totalActiveLeads = useMemo(
        () => groups.reduce((sum, g) => sum + (g.active_leads ?? 0), 0),
        [groups]
    );

    const saveMutation = useMutation({
        mutationFn: (data: { app_id: number; page_ids: string[] }) => saveAppPageGroup({ ...data, userId: donoParam }),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['app-page-groups'] });
            toast({ title: 'Grupo salvo' });
            setEditingAppId(null);
            setCreatingAppId(null);
            setDraftPageIds([]);
        },
        onError: (error: unknown) => {
            const withoutToken = extractPagesWithoutToken(error);
            if (withoutToken) {
                setPagesWithoutToken(withoutToken);
                return;
            }
            const message = error instanceof Error ? error.message : 'Erro ao salvar o grupo.';
            toast({ variant: 'destructive', title: 'Erro', description: message });
        },
    });

    const deleteMutation = useMutation({
        mutationFn: (appId: number) => deleteAppPageGroup(appId, donoParam),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['app-page-groups'] });
            toast({ title: 'Grupo removido' });
            setGroupToDelete(null);
        },
    });

    const removePageMutation = useMutation({
        mutationFn: (pageId: string) => removePageFromGroup(pageId, donoParam),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['app-page-groups'] });
        },
    });

    const startEditing = (appId: number, currentPageIds: string[]) => {
        setEditingAppId(appId);
        setCreatingAppId(null);
        setDraftPageIds(currentPageIds);
        setActiveCategoryIds([]);
        setSelectedConnectedUserId('all');
    };

    const startCreating = (appId: number) => {
        setCreatingAppId(appId);
        setEditingAppId(null);
        setDraftPageIds([]);
        setActiveCategoryIds([]);
        setSelectedConnectedUserId('all');
    };

    const cancelEditing = () => {
        setEditingAppId(null);
        setCreatingAppId(null);
        setDraftPageIds([]);
        setActiveCategoryIds([]);
        setSelectedConnectedUserId('all');
    };

    const isLoading = loadingGroups || loadingApps || loadingPages;

    if (isLoading) {
        return (
            <div className="flex items-center justify-center py-16">
                <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
            </div>
        );
    }

    // Páginas disponíveis para o select: as já livres + as já pertencentes
    // a ESTE app (senão editar um grupo existente as escondia da lista),
    // filtradas por categoria e/ou perfil conectado quando algum filtro está ativo.
    //
    // Exceção: com filtro de perfil ativo, mostramos TAMBÉM páginas já
    // alocadas em outro app — o objetivo de filtrar por perfil é justamente
    // poder mover as páginas dele pra este grupo (salvar já move, ON
    // DUPLICATE KEY UPDATE app_id na constraint page_id+user_id).
    const availablePagesFor = (appId: number | null) =>
        pages.filter(p => {
            if (donoParam != null && p.user_id !== donoParam) return false;
            const isFree = !groupedPageIds.has(p.page_id) || (appId != null && draftPageIds.includes(p.page_id));
            if (!isFree && !connectedUserPageIds) return false;
            if (activeCategoryIds.length > 0 && !(p.internal_category_id != null && activeCategoryIds.includes(String(p.internal_category_id)))) {
                return false;
            }
            if (connectedUserPageIds && !connectedUserPageIds.has(p.page_id)) {
                return false;
            }
            return true;
        });

    const availableOptionsFor = (appId: number | null) =>
        availablePagesFor(appId).map(p => {
            const currentGroup = currentGroupByPageId.get(p.page_id);
            const sublabel = currentGroup && currentGroup.appId !== appId
                ? `${p.page_id} · já em: ${currentGroup.appName ?? `app #${currentGroup.appId}`}`
                : p.page_id;
            return { value: p.page_id, label: p.page_name, sublabel };
        });

    const someFilteredSelected = (appId: number | null) =>
        availablePagesFor(appId).some(p => draftPageIds.includes(p.page_id));

    // Páginas do rascunho atual que já pertencem a outro app — salvar move
    // elas silenciosamente no backend (ON DUPLICATE KEY UPDATE), então
    // avisamos antes de confirmar.
    const getPagesBeingMoved = (appId: number, pageIds: string[]) =>
        pageIds
            .map(pageId => ({ pageId, currentGroup: currentGroupByPageId.get(pageId) }))
            .filter((x): x is { pageId: string; currentGroup: { appId: number; appName: string | null } } =>
                !!x.currentGroup && x.currentGroup.appId !== appId
            );

    const toggleAllFiltered = (appId: number | null) => {
        const filtered = availablePagesFor(appId);
        const filteredIds = new Set(filtered.map(p => p.page_id));
        if (someFilteredSelected(appId)) {
            setDraftPageIds(draftPageIds.filter(id => !filteredIds.has(id)));
        } else {
            const current = new Set(draftPageIds);
            setDraftPageIds([...draftPageIds, ...filtered.filter(p => !current.has(p.page_id)).map(p => p.page_id)]);
        }
    };

    const totalGroupedPages = groupedPageIds.size;

    if (vendoTodos) {
        return (
            <div className="space-y-6">
                <div className="flex flex-wrap items-start justify-between gap-3">
                    <div>
                        <h2 className="text-lg font-semibold text-gray-900">Grupos de App</h2>
                        <p className="text-sm text-gray-500 mt-1 max-w-2xl">
                            Grupos de todos os usuários. Para editar, escolha o usuário no filtro.
                        </p>
                    </div>
                    <FiltroUsuarioAdmin className="w-60" />
                </div>
                {loadingGroups ? (
                    <div className="flex items-center gap-2 text-sm text-gray-500">
                        <Loader2 className="h-4 w-4 animate-spin" /> Carregando grupos…
                    </div>
                ) : groups.length === 0 ? (
                    <p className="text-sm text-gray-500">Nenhum usuário tem Grupo de App.</p>
                ) : (
                    <div className="space-y-3">
                        {groups.map((g) => (
                            <div key={`${g.app_id}-${g.user_id}`} className="rounded-xl border border-gray-200 bg-white p-4">
                                <div className="flex flex-wrap items-center gap-2">
                                    <AppWindow className="h-4 w-4 text-gray-500" />
                                    <span className="font-medium text-gray-900">{g.app_name ?? `App #${g.app_id}`}</span>
                                    {g.owner_name && (
                                        <span className="rounded-md bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-600">{g.owner_name}</span>
                                    )}
                                    <span className="rounded-full bg-emerald-50 px-2 py-0.5 text-[11px] font-medium text-emerald-600">
                                        {g.pages.length} página{g.pages.length !== 1 ? 's' : ''}
                                    </span>
                                    <span className="text-xs text-gray-500 tabular-nums">
                                        {formatLeads(g.active_leads ?? 0)} leads ativos
                                    </span>
                                </div>
                                <p className="mt-2 text-xs text-gray-500">
                                    {g.pages.slice(0, 12).map((p) => p.page_name ?? p.page_id).join(' · ')}
                                    {g.pages.length > 12 ? ` · e mais ${g.pages.length - 12}` : ''}
                                </p>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        );
    }

    return (
        <div className="space-y-6">
            <div>
                <div className="flex flex-wrap items-center justify-between gap-3">
                    <h2 className="text-lg font-semibold text-gray-900">Grupos de App</h2>
                    <FiltroUsuarioAdmin className="w-60" onChange={() => {
                        setEditingAppId(null);
                        setCreatingAppId(null);
                        setDraftPageIds([]);
                    }} />
                </div>
                <p className="text-sm text-gray-500 mt-1 max-w-2xl">
                    Associe manualmente um app a um conjunto de páginas para garantir que os envios (broadcast e
                    aprovação de utility templates) sempre usem esse app — evita concentrar páginas demais no
                    mesmo app e estourar o limite de taxa da Meta.
                </p>
            </div>

            {activeApps.length > 0 && (
                <div className="flex items-center gap-4 rounded-xl border border-gray-100 bg-gray-50/60 px-4 py-3">
                    <div className="flex items-baseline gap-1.5">
                        <span className="text-xl font-semibold text-gray-900 tabular-nums">{groups.length}</span>
                        <span className="text-xs text-gray-500">de {activeApps.length} apps com grupo</span>
                    </div>
                    <div className="h-4 w-px bg-gray-200" />
                    <div className="flex items-baseline gap-1.5">
                        <span className="text-xl font-semibold text-gray-900 tabular-nums">{totalGroupedPages}</span>
                        <span className="text-xs text-gray-500">página{totalGroupedPages !== 1 ? 's' : ''} associada{totalGroupedPages !== 1 ? 's' : ''}</span>
                    </div>
                    <div className="h-4 w-px bg-gray-200" />
                    <div className="flex items-baseline gap-1.5">
                        <span className="text-xl font-semibold text-gray-900 tabular-nums">{formatLeads(totalActiveLeads)}</span>
                        <span className="text-xs text-gray-500">leads ativos nos grupos</span>
                    </div>
                </div>
            )}

            <div className="space-y-3">
                {activeApps.map(app => {
                    const group = groups.find(g => g.app_id === app.id);
                    const hasGroup = !!group;
                    const isEditing = editingAppId === app.id;
                    const isCreating = creatingAppId === app.id;
                    const isExpanded = isEditing || isCreating;
                    const pageIds = group ? group.pages.map(p => p.page_id) : [];
                    const appLeads = group?.active_leads ?? 0;
                    const share = totalActiveLeads > 0 ? (appLeads / totalActiveLeads) * 100 : 0;

                    return (
                        <div
                            key={app.id}
                            className={`rounded-xl border bg-white transition-colors ${
                                isExpanded
                                    ? 'border-[#7dd3fc] ring-1 ring-[#7dd3fc]/40'
                                    : hasGroup
                                        ? 'border-gray-200'
                                        : 'border-dashed border-gray-200'
                            }`}
                        >
                            <div className="flex items-center justify-between gap-3 p-4">
                                <div className="flex min-w-0 items-center gap-3">
                                    <span
                                        className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg ${
                                            hasGroup ? 'bg-[#0f172a] text-white' : 'bg-gray-100 text-gray-400'
                                        }`}
                                    >
                                        <AppWindow className="h-4.5 w-4.5" />
                                    </span>
                                    <div className="min-w-0">
                                        <div className="flex items-center gap-2">
                                            <span className="truncate font-medium text-gray-900">{app.app_name}</span>
                                            {app.purpose && app.purpose !== 'both' && (
                                                <span
                                                    className={`shrink-0 rounded-full px-2 py-0.5 text-[11px] font-medium ${
                                                        app.purpose === 'broadcast'
                                                            ? 'bg-blue-50 text-blue-700'
                                                            : 'bg-purple-50 text-purple-700'
                                                    }`}
                                                >
                                                    Dedicado a {app.purpose === 'broadcast' ? 'Broadcast' : 'Funil'}
                                                </span>
                                            )}
                                            {hasGroup ? (
                                                <span className="flex shrink-0 items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 text-[11px] font-medium text-emerald-600">
                                                    <CheckCircle2 className="h-3 w-3" />
                                                    {pageIds.length} página{pageIds.length !== 1 ? 's' : ''}
                                                </span>
                                            ) : (
                                                <span className="shrink-0 rounded-full bg-gray-100 px-2 py-0.5 text-[11px] font-medium text-gray-400">
                                                    Sem grupo
                                                </span>
                                            )}
                                        </div>
                                        <span className="text-xs text-gray-400">{app.app_id}</span>
                                    </div>
                                </div>

                                <div className="flex shrink-0 items-center gap-2">
                                    {hasGroup && (
                                        <div className="mr-2 hidden w-44 sm:block" title="Leads ativos das páginas deste grupo — atualizado a cada 10 min">
                                            <div className="flex items-baseline justify-between gap-2">
                                                <span className="text-sm font-semibold text-gray-900 tabular-nums">
                                                    {formatLeads(appLeads)}
                                                </span>
                                                <span className="text-[11px] text-gray-500 tabular-nums">
                                                    {share.toLocaleString('pt-BR', { maximumFractionDigits: 1 })}% do envio
                                                </span>
                                            </div>
                                            <div className="mt-1 h-1.5 w-full overflow-hidden rounded-full bg-gray-100">
                                                <div className="h-full rounded-full bg-[#0f172a]" style={{ width: `${share}%` }} />
                                            </div>
                                            <span className="mt-0.5 block text-[11px] text-gray-400">leads ativos</span>
                                        </div>
                                    )}
                                    {hasGroup && !isExpanded && (
                                        <Button size="sm" variant="outline" onClick={() => startEditing(app.id, pageIds)}>
                                            Editar
                                        </Button>
                                    )}
                                    {hasGroup && (
                                        <Button
                                            size="sm"
                                            variant="ghost"
                                            className="text-red-500 hover:text-red-600 hover:bg-red-50"
                                            onClick={() => setGroupToDelete(app.id)}
                                            aria-label={`Remover grupo de ${app.app_name}`}
                                        >
                                            <Trash2 className="w-4 h-4" />
                                        </Button>
                                    )}
                                    {!hasGroup && !isExpanded && (
                                        <Button size="sm" variant="outline" className="gap-1.5" onClick={() => startCreating(app.id)}>
                                            <Plus className="w-3.5 h-3.5" />
                                            Criar grupo
                                        </Button>
                                    )}
                                    {isExpanded && (
                                        <Button size="sm" variant="ghost" onClick={cancelEditing} aria-label="Fechar">
                                            <X className="w-4 h-4" />
                                        </Button>
                                    )}
                                </div>
                            </div>

                            {isExpanded ? (
                                <div className="space-y-3 border-t border-gray-100 px-4 pb-4 pt-3">
                                    {(categoryOptions.length > 0 || connectedUserOptions.length > 0) && (
                                        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                                            {categoryOptions.length > 0 && (
                                                <SearchableMultiSelect
                                                    options={categoryOptions}
                                                    values={activeCategoryIds}
                                                    onValuesChange={setActiveCategoryIds}
                                                    placeholder="Filtrar por categoria..."
                                                    searchPlaceholder="Buscar categoria..."
                                                    selectedLabel={n => `${n} categoria${n > 1 ? 's' : ''}`}
                                                />
                                            )}
                                            {connectedUserOptions.length > 0 && (
                                                <SearchableSelect
                                                    value={selectedConnectedUserId}
                                                    onValueChange={setSelectedConnectedUserId}
                                                    options={[{ value: 'all', label: 'Todos os perfis' }, ...connectedUserOptions]}
                                                    placeholder="Conectado por..."
                                                />
                                            )}
                                        </div>
                                    )}
                                    <div className="flex items-center justify-between">
                                        <span className="text-xs font-medium text-gray-500">
                                            {availableOptionsFor(hasGroup ? app.id : null).length} página(s) disponível(is)
                                        </span>
                                        {(activeCategoryIds.length > 0 || selectedConnectedUserId !== 'all') && (
                                            <Button
                                                type="button"
                                                variant="ghost"
                                                size="sm"
                                                onClick={() => toggleAllFiltered(hasGroup ? app.id : null)}
                                                className="h-7 text-xs text-[#0f172a] hover:bg-[#7dd3fc]/10"
                                            >
                                                {someFilteredSelected(hasGroup ? app.id : null) ? 'Desmarcar filtradas' : 'Selecionar filtradas'}
                                            </Button>
                                        )}
                                    </div>
                                    {selectedConnectedUserId !== 'all' && (() => {
                                        const currentAppId = hasGroup ? app.id : null;
                                        const inOtherApp = availableOptionsFor(currentAppId)
                                            .map(o => currentGroupByPageId.get(o.value))
                                            .filter((g): g is { appId: number; appName: string | null } => !!g && g.appId !== currentAppId);
                                        if (inOtherApp.length === 0) return null;
                                        const countByApp = new Map<string, number>();
                                        for (const g of inOtherApp) {
                                            const label = g.appName ?? `app #${g.appId}`;
                                            countByApp.set(label, (countByApp.get(label) ?? 0) + 1);
                                        }
                                        const breakdown = Array.from(countByApp.entries())
                                            .map(([appName, count]) => `${count} de ${appName}`)
                                            .join(', ');
                                        return (
                                            <div className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
                                                <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
                                                <span>
                                                    {inOtherApp.length} página{inOtherApp.length > 1 ? 's' : ''} deste perfil já{' '}
                                                    {inOtherApp.length > 1 ? 'pertencem' : 'pertence'} a outro app ({breakdown}) — selecionar e salvar{' '}
                                                    {inOtherApp.length > 1 ? 'vai movê-las' : 'vai movê-la'} para este grupo.
                                                </span>
                                            </div>
                                        );
                                    })()}
                                    <SearchableMultiSelect
                                        options={availableOptionsFor(hasGroup ? app.id : null)}
                                        values={draftPageIds}
                                        onValuesChange={setDraftPageIds}
                                        placeholder="Selecione as páginas..."
                                        searchPlaceholder="Buscar página..."
                                        showTags={false}
                                        selectedLabel={n => `${n} página${n > 1 ? 's' : ''} selecionada${n > 1 ? 's' : ''}`}
                                    />
                                    <div className="flex justify-end gap-2">
                                        <Button size="sm" variant="ghost" onClick={cancelEditing}>
                                            Cancelar
                                        </Button>
                                        <Button
                                            size="sm"
                                            onClick={() => {
                                                const beingMoved = getPagesBeingMoved(app.id, draftPageIds);
                                                if (beingMoved.length > 0) {
                                                    setPendingSave({ appId: app.id, pageIds: draftPageIds });
                                                    return;
                                                }
                                                saveMutation.mutate({ app_id: app.id, page_ids: draftPageIds });
                                            }}
                                            disabled={saveMutation.isPending || (!hasGroup && draftPageIds.length === 0)}
                                        >
                                            {saveMutation.isPending ? (
                                                <Loader2 className="w-4 h-4 animate-spin" />
                                            ) : hasGroup ? (
                                                'Salvar'
                                            ) : (
                                                'Criar grupo'
                                            )}
                                        </Button>
                                    </div>
                                </div>
                            ) : hasGroup && pageIds.length > 0 ? (
                                <div className="flex flex-wrap gap-1.5 border-t border-gray-100 px-4 pb-4 pt-3">
                                    {[...group!.pages].sort((a, b) => (b.active_leads ?? 0) - (a.active_leads ?? 0)).map(p => (
                                        <span
                                            key={p.page_id}
                                            className="inline-flex items-center gap-1.5 rounded-full bg-gray-100 pl-2.5 pr-1.5 py-1 text-xs text-gray-700"
                                        >
                                            <span>{p.page_name ?? p.page_id}</span>
                                            <span className="tabular-nums text-gray-400">{formatLeads(p.active_leads ?? 0)}</span>
                                            <button
                                                type="button"
                                                onClick={() => removePageMutation.mutate(p.page_id)}
                                                className="hover:text-red-500"
                                                aria-label={`Remover ${p.page_name ?? p.page_id} do grupo`}
                                            >
                                                <X className="w-3 h-3" />
                                            </button>
                                        </span>
                                    ))}
                                </div>
                            ) : null}
                        </div>
                    );
                })}

                {activeApps.length === 0 && (
                    <div className="rounded-xl border border-dashed border-gray-200 p-8 text-center text-sm text-gray-400">
                        Nenhum app ativo — conecte um app na aba "Contas e Usuários" primeiro.
                    </div>
                )}
            </div>

            <ConfirmDialog
                open={groupToDelete != null}
                onOpenChange={open => !open && setGroupToDelete(null)}
                title="Remover Grupo de App"
                description="As páginas voltam a usar a distribuição automática de app no próximo envio. Deseja continuar?"
                confirmLabel="Remover"
                variant="danger"
                onConfirm={() => groupToDelete != null && deleteMutation.mutate(groupToDelete)}
                isLoading={deleteMutation.isPending}
            />

            <ConfirmDialog
                open={pendingSave != null}
                onOpenChange={open => !open && setPendingSave(null)}
                title="Mover página(s) de app"
                description={
                    pendingSave
                        ? (() => {
                              const moved = getPagesBeingMoved(pendingSave.appId, pendingSave.pageIds);
                              const names = moved
                                  .map(m => `${pages.find(p => p.page_id === m.pageId)?.page_name ?? m.pageId} (de: ${m.currentGroup.appName ?? `app #${m.currentGroup.appId}`})`)
                                  .join(', ');
                              return `${moved.length} página${moved.length > 1 ? 's' : ''} ${moved.length > 1 ? 'serão movidas' : 'será movida'} do app atual para este grupo: ${names}. Deseja continuar?`;
                          })()
                        : undefined
                }
                confirmLabel="Mover e salvar"
                variant="warning"
                onConfirm={() => {
                    if (!pendingSave) return;
                    saveMutation.mutate({ app_id: pendingSave.appId, page_ids: pendingSave.pageIds });
                    setPendingSave(null);
                }}
                isLoading={saveMutation.isPending}
            />

            <Dialog open={pagesWithoutToken != null} onOpenChange={open => !open && setPagesWithoutToken(null)}>
                <DialogContent className="sm:max-w-md">
                    <DialogHeader>
                        <div className="flex items-center gap-2">
                            <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-amber-50 text-amber-500">
                                <AlertTriangle className="h-4 w-4" />
                            </span>
                            <DialogTitle className="text-sm font-semibold text-gray-900">
                                Página(s) sem token para este app
                            </DialogTitle>
                        </div>
                    </DialogHeader>
                    <p className="text-sm text-gray-500 -mt-1">
                        Essas páginas ainda não têm token para este app. Conecte o app a elas
                        (aba "Contas e Usuários") antes de incluí-las no grupo.
                    </p>
                    <div className="max-h-64 space-y-1.5 overflow-y-auto rounded-lg border border-gray-100 bg-gray-50/60 p-2">
                        {pagesWithoutToken?.map(p => (
                            <div
                                key={p.page_id}
                                className="flex items-center justify-between gap-3 rounded-md bg-white px-3 py-2 text-sm shadow-sm"
                            >
                                <span className="truncate font-medium text-gray-800">
                                    {p.page_name ?? 'Página sem nome'}
                                </span>
                                <span className="shrink-0 font-mono text-xs text-gray-400">{p.page_id}</span>
                            </div>
                        ))}
                    </div>
                    <DialogFooter>
                        <Button size="sm" onClick={() => setPagesWithoutToken(null)}>
                            Entendi
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}

function formatLeads(n: number): string {
    return n.toLocaleString('pt-BR');
}
